import React from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useNavigate } from 'react-router-dom';

import { useRequiredHermes } from 'ha/modules/Hermes';
import { PAYMENTS } from 'ha/constants/pageNames';
import { Page } from 'ha/modules/Page';
import { BreadcrumbsWrapper, Container, units } from '@hbf/dsl';

import { HermesMeta } from 'ha/components.legacy/HermesMeta';
import { Breadcrumbs } from 'ha/components/Breadcrumbs';
import { useIntl } from 'ha/i18n';
import { Label, StyledTab, StyledTabs } from 'ha/components/StyledTabs';
import { ValuePropositionSwitch } from 'ha/components/ValuePropositions/ValuePropositionSwitch';

import { AboveTheFold } from './AboveTheFold';
import { HermesData } from '../types';
import { KEYS } from '../constants';

const useStyles = makeStyles()((theme: Theme) => ({
  vp: {
    marginTop: units.rem(10),
    [theme.breakpoints.down('md')]: {
      marginTop: units.rem(5),
    },
  },
  container: {
    backgroundColor: 'rgb(244,248,251)',
    display: 'flex',
    paddingBottom: units.rem(10),
    [theme.breakpoints.down('md')]: {
      paddingBottom: units.rem(5),
    },
  },
}));

export const PaymentsPage: React.FC = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { T, urlResolver } = useIntl();
  const {
    data: { pageType, valuePropositions },
    route,
    linkData,
    info,
  } = useRequiredHermes<HermesData>();

  const isTenant = pageType === 'tenant';
  const [currentTabIndex, setCurrentTab] = React.useState(isTenant ? 0 : 1);

  const handleTabChange = React.useCallback(
    (_: React.ChangeEvent<HTMLSelectElement>, tabIndex: number) => {
      setCurrentTab(tabIndex);

      const nextLocation =
        tabIndex === 0
          ? urlResolver.getPaymentsTenant()
          : urlResolver.getPaymentsLandlord();

      navigate(nextLocation);
    },
    [navigate, urlResolver],
  );

  const breadcrumbs = [
    {
      name: T(KEYS[pageType].breadcrumb),
      path: urlResolver.absolutizePath(route.pathname, {
        skipAddLanguage: true,
      }),
    },
  ];

  return (
    <Page name={PAYMENTS} hideIntercom>
      <BreadcrumbsWrapper>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
      </BreadcrumbsWrapper>
      <HermesMeta
        metaTitle={T(KEYS[pageType].meta.title)}
        metaDescription={T(KEYS[pageType].meta.description)}
        linkData={linkData}
        info={info}
      />
      <React.Fragment>
        <AboveTheFold
          title={T('pmp.online_rent_collection.heading')}
          description={T('pmp.online_rent_collection.intro')}
        >
          <StyledTabs value={currentTabIndex} onChange={handleTabChange}>
            <StyledTab
              aria-label={T('pmp.renting_out.for_tenants')}
              label={<Label>{T('pmp.renting_out.for_tenants')}</Label>}
            />
            <StyledTab
              aria-label={T('pmp.renting_out.for_advertisers')}
              label={<Label>{T('pmp.renting_out.for_advertisers')}</Label>}
            />
          </StyledTabs>
        </AboveTheFold>
        <div className={classes.container}>
          <Container maxWidth="md">
            {valuePropositions.map((vp, index) => (
              <div className={classes.vp} key={index}>
                <ValuePropositionSwitch vp={vp} />
              </div>
            ))}
          </Container>
        </div>
      </React.Fragment>
    </Page>
  );
};
